import { GameStatus, GameSummary, Matchup } from './game-api.types';

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  JPY: '¥',
  BRL: 'R$',
  SEK: 'KR',
  NOK: 'KR',
  DKK: 'KR',
  CHF: 'CHF',
  PLN: 'zł',
  TRY: '₺',
  CAD: 'CA$',
  AUD: 'A$',
  INR: '₹',
};

function currencySymbol(code?: string | null): string {
  if (!code) return '';
  return CURRENCY_SYMBOLS[code.toUpperCase()] ?? code;
}

export interface CompetitorDto {
  uuid?: string;
  name?: string;
  shortName?: string;
  logo?: { url?: string } | null;
}

export interface ParticipantDto {
  competitor1?: CompetitorDto;
  competitor2?: CompetitorDto;
  matchTime?: string;
}

export interface PrizeValueDto {
  value?: number;
  label?: string;
  currency?: string;
}

export interface QuizDto {
  uuid: string;
  name?: string;
  title?: string | null;
  subtitle?: string | null;
  metaParticipants?: number;
  format?: string;
  backgroundAsset?: { url?: string } | string | null;
  quizGameBackground?: { url?: string } | string | null;
  cardCount?: number;
  ended?: boolean;
  settled?: boolean;
  paused?: boolean;
  startTs?: string;
  endTz?: string;
  endTs?: string;
  prizeValue?: PrizeValueDto;
  quizGameUrl?: string;
  participants?: ParticipantDto[];
  hasPlayedQuizGame?: boolean;
  theme?: {
    explainerBackgroundAsset?: { url?: string } | null;
    clientLogoAsset?: { url?: string };
  };
}

function competitorToTeam(c?: CompetitorDto): { name: string; icon: string } {
  return { name: c?.name ?? c?.shortName ?? '', icon: c?.logo?.url ?? '' };
}

function buildMatchups(participants?: ParticipantDto[]): Matchup[] {
  if (!participants?.length) return [];
  return participants.map((p) => ({
    home: competitorToTeam(p.competitor1),
    away: competitorToTeam(p.competitor2),
    time: p.matchTime,
  }));
}

function quizStatus(q: QuizDto): GameStatus {
  if (q.settled) return 'game-ended';
  if (q.ended) return q.hasPlayedQuizGame ? 'pending' : 'game-ended';
  if (q.paused) return 'pending';
  const now = Date.now();
  const start = q.startTs ? Date.parse(q.startTs) : 0;
  const endStr = q.endTz || q.endTs;
  const end = endStr ? Date.parse(endStr) : Infinity;
  if (now < start) return 'upcoming';
  if (now > end) return q.hasPlayedQuizGame ? 'pending' : 'game-ended';
  return q.hasPlayedQuizGame ? 'played' : 'live';
}

function backgroundUrl(asset: QuizDto['backgroundAsset']): string | undefined {
  if (!asset) return undefined;
  return typeof asset === 'string' ? asset : asset.url;
}

export function mapQuizToGameSummary(q: QuizDto): GameSummary {
  const sym = currencySymbol(q.prizeValue?.currency);
  const prizeLabel =
    q.prizeValue?.value 
      ? `${sym}${q.prizeValue.value.toLocaleString()}`
      : (q.prizeValue?.label ?? '');

  const matchups = buildMatchups(q.participants);
  const emptyMatchup: Matchup = {
    home: { name: '', icon: '' },
    away: { name: '', icon: '' },
  };

  return {
    id: q.uuid,
    title: q.title || q.name || '',
    subtitle: q.subtitle || '',
    prize: 'Prize Pool',
    prizeValue: prizeLabel,
    tag: '',
    format: 'stacked',
    cardCount: q.cardCount || 0,
    deadline: q.endTz || q.endTs || '',
    participants: matchups[0] ?? emptyMatchup,
    matchups,
    backgroundImage:
      backgroundUrl(q.quizGameBackground) ??
      backgroundUrl(q.backgroundAsset) ??
      q.theme?.explainerBackgroundAsset?.url,
    gameLogo: q.theme?.clientLogoAsset?.url,
    status: quizStatus(q),
    metaParticipants: q.metaParticipants,
    metaTime: q.endTz || q.endTs,
    quizGameUrl: q.quizGameUrl,
  };
}
